export const portalTabTypes = ['student', 'class', 'staff'] as const

export type PortalTabType = (typeof portalTabTypes)[number]

export type PortalTabSwitchingMode = 'shared' | 'individual'

export const defaultPortalTabColors: Record<PortalTabType, string> = {
    student: 'teal',
    class: 'violet',
    staff: 'blue',
}

export type PortalTab = {
    id: string
    type: PortalTabType
    resourceId: string
    route: string
}

export type PortalTabsState = {
    tabs: PortalTab[]
    selectedTabId: string | null
    hiddenTypes: PortalTabType[]
    lastRouteByType: Partial<Record<PortalTabType, string>>
    tabsVisible: boolean
    tabsEnabled: boolean
    colorfulTabs: boolean
    showTabIcons: boolean
    tabColors: Record<PortalTabType, string>
    switchingMode: PortalTabSwitchingMode
    newStudentTabRoute: string
}

export const portalTabTypeMeta: Record<PortalTabType, { label: string; color: string }> = {
    student: { label: 'Students', color: defaultPortalTabColors.student },
    class: { label: 'Classes', color: defaultPortalTabColors.class },
    staff: { label: 'Staff', color: defaultPortalTabColors.staff },
}

const defaultRoutes: Record<PortalTabType, string> = {
    student: '/students/$id',
    class: '/groups/classes/$id',
    staff: '/staff/$id',
}

function normalizeResourceId(value: unknown): string | null {
    if (typeof value !== 'string' && typeof value !== 'number') {
        return null
    }

    const resourceId = String(value).trim()
    return resourceId.length > 0 ? resourceId : null
}

export function getPortalTabId(type: PortalTabType, resourceId: string) {
    return `${type}:${resourceId}`
}

function routeForResource(type: PortalTabType, resourceId: string, routePattern?: string) {
    return (routePattern || defaultRoutes[type]).replace('$id', encodeURIComponent(resourceId))
}

function routePatternForResource(type: PortalTabType, resourceId: string, route: string) {
    const encoded = encodeURIComponent(resourceId)
    const pattern = route.replace(resourceId, '$id').replace(encoded, '$id')

    // A conversation belongs to a specific student. Keep the conversations
    // section selected when another student is opened, but do not reuse the
    // previous student's conversation identifier.
    if (type === 'student') {
        return pattern.replace(/^(\/students\/\$id\/conversations)\/[^/?#]+.*$/, '$1')
    }

    return pattern
}

function normalizeRoutePattern(type: PortalTabType, routePattern: string | undefined) {
    if (!routePattern) {
        return defaultRoutes[type]
    }

    return routePattern.includes('$id') ? routePattern : defaultRoutes[type]
}

function normalizeTab(value: unknown): PortalTab | null {
    if (!value || typeof value !== 'object') {
        return null
    }

    const tab = value as Partial<PortalTab>
    if (!tab.type || !portalTabTypes.includes(tab.type) || !tab.resourceId) {
        return null
    }

    const resourceId = normalizeResourceId(tab.resourceId)
    if (!resourceId) {
        return null
    }

    const id = getPortalTabId(tab.type, resourceId)
    return {
        id,
        type: tab.type,
        resourceId,
        route: tab.route || routeForResource(tab.type, resourceId),
    }
}

export function normalizePortalTabsState(state: PortalTabsState): PortalTabsState {
    const seen = new Set<string>()
    const tabs = state.tabs.flatMap((tab) => {
        const normalized = normalizeTab(tab)
        if (!normalized || seen.has(normalized.id)) {
            return []
        }

        seen.add(normalized.id)
        return [normalized]
    })

    const selectedTabId = state.selectedTabId && tabs.some((tab) => tab.id === state.selectedTabId)
        ? state.selectedTabId
        : tabs[0]?.id ?? null

    const hiddenTypes = portalTabTypes.filter((type) => state.hiddenTypes.includes(type))
    const lastRouteByType = Object.fromEntries(
        portalTabTypes.map((type) => [type, normalizeRoutePattern(type, state.lastRouteByType[type])]),
    ) as Record<PortalTabType, string>

    return {
        tabs,
        selectedTabId,
        hiddenTypes,
        lastRouteByType,
        tabsVisible: state.tabsVisible ?? true,
        tabsEnabled: state.tabsEnabled ?? true,
        colorfulTabs: state.colorfulTabs ?? true,
        showTabIcons: state.showTabIcons ?? false,
        tabColors: { ...defaultPortalTabColors, ...state.tabColors },
        switchingMode: state.switchingMode === 'individual' ? 'individual' : 'shared',
        newStudentTabRoute: state.newStudentTabRoute || 'last',
    }
}

export function openPortalTab(
    state: PortalTabsState,
    type: PortalTabType,
    resourceId: string,
    routePattern?: string,
): PortalTabsState {
    const normalizedResourceId = normalizeResourceId(resourceId)
    if (!normalizedResourceId) {
        return normalizePortalTabsState(state)
    }

    const current = normalizePortalTabsState(state)
    const id = getPortalTabId(type, normalizedResourceId)
    const existing = current.tabs.find((tab) => tab.id === id)
    const pattern = normalizeRoutePattern(type, routePattern || current.lastRouteByType[type])
    const route = existing && !routePattern ? existing.route : routeForResource(type, normalizedResourceId, pattern)
    const tabs = existing
        ? current.tabs.map((tab) => tab.id === id ? { ...tab, route } : tab)
        : [...current.tabs, { id, type, resourceId: normalizedResourceId, route }]

    return normalizePortalTabsState({
        ...current,
        tabs,
        selectedTabId: id,
        hiddenTypes: current.hiddenTypes.filter((hiddenType) => hiddenType !== type),
        lastRouteByType: { ...current.lastRouteByType, [type]: pattern },
    })
}

export function addPortalTab(state: PortalTabsState, type: PortalTabType, resourceId: string, routePattern?: string): PortalTabsState {
    const normalizedResourceId = normalizeResourceId(resourceId)
    if (!normalizedResourceId) {
        return normalizePortalTabsState(state)
    }

    const current = normalizePortalTabsState(state)
    const id = getPortalTabId(type, normalizedResourceId)
    if (current.tabs.some((tab) => tab.id === id)) {
        return current
    }

    return normalizePortalTabsState({
        ...current,
        tabs: [
            ...current.tabs,
            {
                id,
                type,
                resourceId: normalizedResourceId,
                route: routeForResource(type, normalizedResourceId, routePattern || current.lastRouteByType[type]),
            },
        ],
        selectedTabId: current.selectedTabId ?? id,
        hiddenTypes: current.hiddenTypes.filter((hiddenType) => hiddenType !== type),
    })
}

export function openPortalTabs(state: PortalTabsState, type: PortalTabType, resourceIds: string[]): PortalTabsState {
    return resourceIds.reduce(
        (current, resourceId) => openPortalTab(current, type, resourceId),
        normalizePortalTabsState(state),
    )
}

export function selectPortalTab(state: PortalTabsState, tabId: string): PortalTabsState {
    const current = normalizePortalTabsState(state)
    if (!current.tabs.some((tab) => tab.id === tabId)) {
        return current
    }

    return normalizePortalTabsState({ ...current, selectedTabId: tabId })
}

export function closePortalTab(state: PortalTabsState, tabId: string): PortalTabsState {
    const current = normalizePortalTabsState(state)
    const index = current.tabs.findIndex((tab) => tab.id === tabId)
    if (index === -1) {
        return current
    }

    const tabs = current.tabs.filter((tab) => tab.id !== tabId)
    const selectedTabId = current.selectedTabId === tabId
        ? tabs[Math.min(index, tabs.length - 1)]?.id ?? null
        : current.selectedTabId

    return normalizePortalTabsState({ ...current, tabs, selectedTabId })
}

export function closeOtherPortalTabs(state: PortalTabsState, tabId: string, type?: PortalTabType): PortalTabsState {
    const current = normalizePortalTabsState(state)
    const target = current.tabs.find((tab) => tab.id === tabId)
    if (!target) {
        return current
    }

    const tabs = current.tabs.filter((tab) => tab.id === tabId || (type && tab.type !== type))
    return normalizePortalTabsState({ ...current, tabs, selectedTabId: tabId })
}

export function closePortalTabsToSide(
    state: PortalTabsState,
    tabId: string,
    side: 'left' | 'right',
    type?: PortalTabType,
): PortalTabsState {
    const current = normalizePortalTabsState(state)
    const index = current.tabs.findIndex((tab) => tab.id === tabId)
    if (index === -1) {
        return current
    }

    const tabs = current.tabs.filter((tab, tabIndex) => {
        if (tab.id === tabId) {
            return true
        }

        if (type && tab.type !== type) {
            return true
        }

        return side === 'left' ? tabIndex >= index : tabIndex <= index
    })

    return normalizePortalTabsState({ ...current, tabs, selectedTabId: tabId })
}

export function closePortalTabsByType(state: PortalTabsState, type?: PortalTabType): PortalTabsState {
    const current = normalizePortalTabsState(state)
    const tabs = type ? current.tabs.filter((tab) => tab.type !== type) : []
    const selectedTabId = current.selectedTabId && tabs.some((tab) => tab.id === current.selectedTabId)
        ? current.selectedTabId
        : tabs[0]?.id ?? null

    return normalizePortalTabsState({ ...current, tabs, selectedTabId })
}

export function selectAdjacentPortalTab(state: PortalTabsState, direction: -1 | 1): PortalTabsState {
    const current = normalizePortalTabsState(state)
    const visibleTabs = current.tabs.filter((tab) => !current.hiddenTypes.includes(tab.type))
    if (!current.selectedTabId || visibleTabs.length === 0) {
        return current
    }

    const index = visibleTabs.findIndex((tab) => tab.id === current.selectedTabId)
    const nextIndex = Math.max(0, Math.min(visibleTabs.length - 1, index + direction))

    return normalizePortalTabsState({
        ...current,
        selectedTabId: visibleTabs[nextIndex]?.id ?? current.selectedTabId,
    })
}

export function reorderPortalTabs(state: PortalTabsState, activeTabId: string, overTabId: string): PortalTabsState {
    const current = normalizePortalTabsState(state)
    const activeIndex = current.tabs.findIndex((tab) => tab.id === activeTabId)
    const overIndex = current.tabs.findIndex((tab) => tab.id === overTabId)

    if (activeIndex === -1 || overIndex === -1 || activeIndex === overIndex) {
        return current
    }

    const tabs = [...current.tabs]
    const [movedTab] = tabs.splice(activeIndex, 1)
    if (!movedTab) {
        return current
    }

    tabs.splice(overIndex, 0, movedTab)
    return normalizePortalTabsState({ ...current, tabs })
}

export function syncPortalTabRoute(
    state: PortalTabsState,
    type: PortalTabType,
    resourceId: string,
    route: string,
): PortalTabsState {
    const normalizedResourceId = normalizeResourceId(resourceId)
    if (!normalizedResourceId) {
        return normalizePortalTabsState(state)
    }

    const current = normalizePortalTabsState(state)
    const id = getPortalTabId(type, normalizedResourceId)
    const pattern = routePatternForResource(type, normalizedResourceId, route)
    const tabs = current.tabs.some((tab) => tab.id === id)
        ? current.tabs.map((tab) => {
            if (tab.id === id || (current.switchingMode === 'shared' && tab.type === type)) {
                return { ...tab, route: routeForResource(type, tab.resourceId, pattern) }
            }

            return tab
        })
        : [...current.tabs, { id, type, resourceId: normalizedResourceId, route }]

    return normalizePortalTabsState({
        ...current,
        tabs,
        selectedTabId: id,
        hiddenTypes: current.hiddenTypes.filter((hiddenType) => hiddenType !== type),
        lastRouteByType: { ...current.lastRouteByType, [type]: pattern },
    })
}

export function setPortalTabTypeVisible(
    state: PortalTabsState,
    type: PortalTabType,
    visible: boolean,
): PortalTabsState {
    const current = normalizePortalTabsState(state)
    return normalizePortalTabsState({
        ...current,
        hiddenTypes: visible
            ? current.hiddenTypes.filter((hiddenType) => hiddenType !== type)
            : [...new Set([...current.hiddenTypes, type])],
    })
}
